import { getCadTextSyncStats } from '@/plugins/cad/diagnostics/cadTextSync'
import { getLoggerManager } from '@/shared/logging'
import { LogLevel } from '@/shared/logging/LogLevel'
import type { CadEntityStats } from '../module/CADModule/utils/cadDiagnostics'

export interface CadFrameTiming {
  totalMs: number
  resetStateMs: number
  sceneRenderMs: number
  dashScaleMs: number
}

export interface CadFrameSnapshot {
  frameNumber: number
  viewId: string
  phase: string
  visibleAssets: number
  visibleEntities: number
  visibleTextEntities: number
  renderables: number
  drawCalls: number
  batchCount: number
  materialBatchCount: number
  materialBatches: Record<string, number>
  entityTypes: Record<string, { entities: number; renderables: number; drawCalls: number }>
  timing: CadFrameTiming
  /** 诊断开启期间的累计文字同步数据，异步耗时含队列等待。 */
  textSync?: ReturnType<typeof getCadTextSyncStats>
  /** 当前可见资产内的文字总数、可见文字及可见高亮轮廓。 */
  textObjects?: { total: number; visible: number; outlines: number }
}

interface FrameState extends CadFrameSnapshot {
  startedAt: number
}

/**
 * CAD 帧级性能诊断。
 *
 * 诊断默认关闭。关闭时 beginFrame 直接返回 null，渲染热路径不会遍历实体或读取
 * renderer.info；开启时只保留最近一个 frame snapshot，避免逐对象/逐帧刷日志。
 */
export class CadPerformanceDiagnostics {
  private readonly log = getLoggerManager().getLogger('cad:diagnostics')
  private lastSnapshot: CadFrameSnapshot | null = null

  isEnabled(): boolean {
    const manager = getLoggerManager()
    return manager.isPerfEnabled() || this.log.isEnabled(LogLevel.DEBUG)
  }

  beginFrame(params: {
    frameNumber: number
    viewId: string
    phase: string
    visibleAssets: number
    visibleEntities: number
    visibleTextEntities: number
    renderables?: number
    renderableTypes?: Record<string, number | { renderables: number; drawCalls: number }>
    materialBatches?: Record<string, number>
    textObjects?: { total: number; visible: number; outlines: number }
    entityStats: readonly CadEntityStats[]
  }): FrameState | null {
    if (!this.isEnabled()) {
      return null
    }

    const entityTypes: Record<
      string,
      { entities: number; renderables: number; drawCalls: number }
    > = {}
    for (const stats of params.entityStats) {
      // 这里的 typeSummary 是加载统计摘要，逐类型 renderable 数量由后续批处理索引补充。
      // 保留摘要字段可让诊断在批处理启用前也能区分实体组成。
      if (stats.typeSummary) {
        for (const entry of stats.typeSummary.split('|')) {
          const separator = entry.lastIndexOf(':')
          if (separator <= 0) continue
          const type = entry.slice(0, separator)
          const count = Number(entry.slice(separator + 1))
          if (!Number.isFinite(count)) continue
          const current = entityTypes[type] ?? { entities: 0, renderables: 0, drawCalls: 0 }
          current.entities += count
          entityTypes[type] = current
        }
      }
    }
    for (const [type, value] of Object.entries(params.renderableTypes ?? {})) {
      const count = typeof value === 'number' ? value : value.renderables
      const drawCalls = typeof value === 'number' ? value : value.drawCalls
      const current = entityTypes[type] ?? { entities: 0, renderables: 0, drawCalls: 0 }
      current.renderables += count
      current.drawCalls += drawCalls
      entityTypes[type] = current
    }

    const startedAt = getLoggerManager().now()
    return {
      startedAt,
      frameNumber: params.frameNumber,
      viewId: params.viewId,
      phase: params.phase,
      visibleAssets: params.visibleAssets,
      visibleEntities: params.visibleEntities,
      visibleTextEntities: params.visibleTextEntities,
      renderables: params.renderables ?? 0,
      drawCalls: 0,
      batchCount: 0,
      materialBatchCount: 0,
      materialBatches: { ...(params.materialBatches ?? {}) },
      textObjects: params.textObjects,
      entityTypes,
      timing: {
        totalMs: 0,
        resetStateMs: 0,
        sceneRenderMs: 0,
        dashScaleMs: 0,
      },
    }
  }

  finish(
    frame: FrameState | null,
    params: {
      renderables?: number
      drawCalls?: number
      batchCount?: number
      materialBatchCount?: number
      resetStateMs?: number
      sceneRenderMs?: number
      dashScaleMs?: number
    } = {}
  ): CadFrameSnapshot | null {
    if (!frame) return null

    const snapshot: CadFrameSnapshot = {
      ...frame,
      textSync: getCadTextSyncStats(),
      renderables: params.renderables ?? frame.renderables,
      drawCalls: params.drawCalls ?? frame.drawCalls,
      batchCount: params.batchCount ?? frame.batchCount,
      materialBatchCount: params.materialBatchCount ?? frame.materialBatchCount,
      timing: {
        totalMs: getLoggerManager().now() - frame.startedAt,
        resetStateMs: params.resetStateMs ?? 0,
        sceneRenderMs: params.sceneRenderMs ?? 0,
        dashScaleMs: params.dashScaleMs ?? 0,
      },
    }
    this.lastSnapshot = snapshot
    this.log.debug(
      `[CadPerf] view=${snapshot.viewId}, frame=${snapshot.frameNumber}, phase=${snapshot.phase}, assets=${snapshot.visibleAssets}, entities=${snapshot.visibleEntities}, text=${snapshot.visibleTextEntities}, renderables=${snapshot.renderables}, drawCalls=${snapshot.drawCalls}, batches=${snapshot.batchCount}, materials=${snapshot.materialBatchCount}, total=${snapshot.timing.totalMs.toFixed(1)}ms, reset=${snapshot.timing.resetStateMs.toFixed(1)}ms, render=${snapshot.timing.sceneRenderMs.toFixed(1)}ms, dash=${snapshot.timing.dashScaleMs.toFixed(1)}ms`
    )
    return snapshot
  }

  getLastSnapshot(): CadFrameSnapshot | null {
    return this.lastSnapshot
  }
}
